import { useState, useEffect } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import SEOHead from "@/components/SEOHead";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import BlogBreadcrumb from "@/components/blog/BlogBreadcrumb";
import AuthorCard from "@/components/blog/AuthorCard";
import BlogPostCard from "@/components/blog/BlogPostCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";

interface Author {
  id: string;
  name: string;
  slug: string;
  profileImage?: string | null;
  coverImage?: string | null;
  bio?: string | null;
  location?: string | null;
  website?: string | null;
  twitter?: string | null;
  facebook?: string | null;
  postCount: number;
  meta: {
    title: string;
    description: string;
  };
}

interface Post {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  featureImage?: string | null;
  featureImageAlt?: string;
  publishedAt: string;
  readingTime?: number;
  primaryTag?: {
    name: string;
    slug: string;
  } | null;
}

interface Pagination {
  page: number;
  pages: number;
  total: number;
  next: number | null;
  prev: number | null;
}

const BlogAuthor = () => {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const { getLocalizedPath } = useLocalizedPath();
  const [author, setAuthor] = useState<Author | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Determine language from URL
  const isEnglish = location.pathname.startsWith("/en");
  const currentLang = isEnglish ? "en" : "th";

  useEffect(() => {
    const fetchAuthor = async () => {
      if (!slug) return;

      setLoading(true);
      setError(null);

      try {
        const url = new URL(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-blog-author`);
        url.searchParams.set("slug", slug);
        url.searchParams.set("lang", currentLang);

        const response = await fetch(url.toString(), {
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
        });

        const data = await response.json();

        if (!data.success) {
          setError(data.error || "Author not found");
          return;
        }

        setAuthor(data.author);
        setPosts(data.posts || []);
        setPagination(data.pagination);
      } catch (err) {
        console.error("Error fetching author:", err);
        setError("Failed to load author");
      } finally {
        setLoading(false);
      }
    };

    fetchAuthor();
    window.scrollTo(0, 0);
  }, [slug, currentLang]);

  const handleLoadMore = async () => {
    if (!pagination?.next || !slug) return;

    setLoadingMore(true);
    try {
      const url = new URL(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-blog-author`);
      url.searchParams.set("slug", slug);
      url.searchParams.set("page", pagination.next.toString());
      url.searchParams.set("lang", currentLang);

      const response = await fetch(url.toString(), {
        headers: {
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
      });

      const data = await response.json();

      if (data.success) {
        setPosts((prev) => [...prev, ...(data.posts || [])]);
        setPagination(data.pagination);
      }
    } catch (err) {
      console.error("Error loading more posts:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  if (error) {
    return (
      <>
        <SEOHead
          title="Author Not Found | Carbonwize Blog"
          description="Author not found"
          path="/blog"
          noIndex
        />
        <div className="min-h-screen bg-background">
          <Navbar />
          <main className="pt-24 pb-16">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
              <h1 className="text-4xl font-bold text-foreground mb-4">Author Not Found</h1>
              <p className="text-muted-foreground mb-8">{error}</p>
              <Link
                to={getLocalizedPath("/blog")}
                className="inline-flex items-center gap-2 text-primary hover:underline"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Blog
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      {author && (
        <SEOHead
          title={`${author.meta.title} | Carbonwize Blog`}
          description={author.meta.description || author.bio || ""}
          path={`/blog/author/${author.slug}`}
          ogImage={author.profileImage || undefined}
          ogType="website" /* author profile is essentially a website page, or could be 'profile' if supported by SEOHead (it supports website|article) */
        />
      )}

      <div className="min-h-screen bg-background">
        <Navbar />

        <main className="pt-24 pb-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
            {loading ? (
              <div className="space-y-8">
                <Skeleton className="h-4 w-64" />
                <div className="flex flex-col items-center text-center py-8">
                  <Skeleton className="w-24 h-24 rounded-full mb-4" />
                  <Skeleton className="h-8 w-48 mb-2" />
                  <Skeleton className="h-4 w-32 mb-4" />
                  <Skeleton className="h-20 w-full max-w-lg" />
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="bg-card border border-border/50 rounded-2xl overflow-hidden">
                      <Skeleton className="h-48 w-full" />
                      <div className="p-6 space-y-3">
                        <Skeleton className="h-6 w-full" />
                        <Skeleton className="h-4 w-full" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : author ? (
              <>
                <BlogBreadcrumb
                  items={[{ label: "Blog", href: getLocalizedPath("/blog") }, { label: author.name }]}
                />

                {/* Author Profile */}
                <section className="py-12 border-b border-border mb-12">
                  <AuthorCard author={author} variant="full" linked={false} />
                </section>

                {/* Posts */}
                <section>
                  <h2 className="text-2xl font-bold text-foreground mb-8">
                    Articles by {author.name}
                    <span className="text-muted-foreground font-normal ml-2">
                      ({author.postCount})
                    </span>
                  </h2>

                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {posts.map((post) => (
                      <BlogPostCard key={post.id} post={post} showAuthor={false} />
                    ))}
                  </div>

                  {posts.length === 0 && (
                    <div className="text-center py-12">
                      <p className="text-muted-foreground">No articles found.</p>
                    </div>
                  )}

                  {pagination?.next && (
                    <div className="text-center mt-8">
                      <Button
                        variant="outline"
                        size="lg"
                        onClick={handleLoadMore}
                        disabled={loadingMore}
                      >
                        {loadingMore ? "Loading..." : "Load More"}
                      </Button>
                    </div>
                  )}
                </section>
              </>
            ) : null}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default BlogAuthor;
